import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { useCampaignStatusHistory, CampaignStatusEntry, CampaignStatusType } from '@/hooks/useCampaignStatusHistory';
import { Timestamp } from 'firebase/firestore';

// Section order as specified in requirements
const SECTION_ORDER: CampaignStatusType[] = [
  'Broad Status',
  'CA Auto Status',
  'CTA Status',
  'Exact Status',
  'Full Auto Status',
  'Micro Auto Status',
  'PP Exact Status',
  'PP PTA Status',
  'PTA Status',
  'ROS Exact Status',
  'ROS PTA Status',
  'TOS Exact Status',
  'TOS PTA Status'
];

// Get display name for section headers (remove "Status" suffix)
const getSectionDisplayName = (statusType: CampaignStatusType): string => {
  return statusType.replace(' Status', '');
};

// Color coding for status values
const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'enabled':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'paused':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
};

// Format date to MM/DD/YY
const formatDate = (timestamp: Timestamp): string => {
  try {
    const date = timestamp.toDate();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  } catch (error) {
    return '';
  }
};

// Status Badge Component
interface StatusBadgeProps {
  status: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = React.memo(({ status }) => {
  if (!status || status === '-') {
    return <span className="text-gray-400">-</span>;
  }

  return (
    <Badge variant="secondary" className={getStatusColor(status)}>
      {status}
    </Badge>
  );
});

StatusBadge.displayName = 'StatusBadge';

// Section Header Component
interface SectionHeaderProps {
  title: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = React.memo(({ title }) => (
  <TableRow className="bg-orange-100 dark:bg-orange-900/30 border-t-4 border-orange-500">
    <TableCell
      colSpan={5}
      className="text-xl font-bold py-5 px-3 text-orange-900 dark:text-orange-100 text-center"
    >
      {title}
    </TableCell>
  </TableRow>
));

SectionHeader.displayName = 'SectionHeader';

// Status Entry Row Component
interface StatusEntryRowProps {
  entry: CampaignStatusEntry;
  index: number;
}

const StatusEntryRow: React.FC<StatusEntryRowProps> = React.memo(({ entry, index }) => (
  <TableRow className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
    <TableCell className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 text-right w-16">
      {entry.daysAgo}
    </TableCell>
    <TableCell className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 w-20">
      {formatDate(entry.date)}
    </TableCell>
    <TableCell className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 w-24">
      {entry.yourName || '-'}
    </TableCell>
    <TableCell className="px-3 py-3 whitespace-nowrap text-sm w-24">
      <StatusBadge status={entry.newValue} />
    </TableCell>
    <TableCell className="px-3 py-3 text-sm text-gray-900 dark:text-gray-100 break-words w-32">
      {entry.note || '-'}
    </TableCell>
  </TableRow>
));

StatusEntryRow.displayName = 'StatusEntryRow';

// Loading Row Component
const LoadingRow: React.FC = React.memo(() => (
  <TableRow>
    <TableCell className="px-6 py-4">
      <Skeleton className="h-4 w-12" />
    </TableCell>
    <TableCell className="px-6 py-4">
      <Skeleton className="h-4 w-20" />
    </TableCell>
    <TableCell className="px-6 py-4">
      <Skeleton className="h-4 w-24" />
    </TableCell>
    <TableCell className="px-6 py-4">
      <Skeleton className="h-6 w-16" />
    </TableCell>
    <TableCell className="px-6 py-4">
      <Skeleton className="h-4 w-32" />
    </TableCell>
  </TableRow>
));

LoadingRow.displayName = 'LoadingRow';

// Main Dashboard Component
const CampaignStatusHistoryDashboard: React.FC = () => {
  const { selectedProduct } = usePMT3Selection();
  const { data, isLoading, error } = useCampaignStatusHistory(
    selectedProduct?.companyName,
    selectedProduct?.id
  );

  return (
    <div className="flex justify-center">
      <Card className="w-full max-w-3xl">
        <CardHeader className="p-4">
          <CardTitle className="text-2xl font-bold text-center">Campaign Status History</CardTitle>
        </CardHeader>
        <CardContent className="pb-6">
          {error && (
            <div className="text-red-600 dark:text-red-400 mb-4">
              Error loading data: {error.message}
            </div>
          )}
          
          <div className="overflow-x-auto">
            <Table className="w-full">
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap text-xs py-2 px-3 font-bold w-16 text-right">
                Days Ago
              </TableHead>
              <TableHead className="whitespace-nowrap text-xs py-2 px-3 font-bold w-20">
                Date
              </TableHead>
              <TableHead className="whitespace-nowrap text-xs py-2 px-3 font-bold w-24">
                Your Name
              </TableHead>
              <TableHead className="whitespace-nowrap text-xs py-2 px-3 font-bold w-24">
                New Value
              </TableHead>
              <TableHead className="whitespace-nowrap text-xs py-2 px-3 font-bold w-32">
                Note
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Show loading skeletons for each section
              SECTION_ORDER.map((statusType) => (
                <React.Fragment key={`loading-${statusType}`}>
                  <SectionHeader title={getSectionDisplayName(statusType)} />
                  <LoadingRow />
                </React.Fragment>
              ))
            ) : data ? (
              // Show actual data organized by sections
              SECTION_ORDER.map((statusType) => {
                const entries = data[statusType] || [];
                
                return (
                  <React.Fragment key={statusType}>
                    <SectionHeader title={getSectionDisplayName(statusType)} />
                    {entries.length === 0 ? (
                      <TableRow>
                        <TableCell 
                          colSpan={5} 
                          className="px-6 py-4 text-center text-gray-500 dark:text-gray-400 italic"
                        >
                          No status history found
                        </TableCell>
                      </TableRow>
                    ) : (
                      entries.map((entry, index) => (
                        <StatusEntryRow 
                          key={entry.id} 
                          entry={entry} 
                          index={index}
                        />
                      ))
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              // No data state
              <TableRow>
                <TableCell 
                  colSpan={5} 
                  className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                >
                  {selectedProduct 
                    ? 'No campaign status history found for this product.'
                    : 'Please select a product to view campaign status history.'
                  }
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CampaignStatusHistoryDashboard;